// COMPONENT IMPORTS
import { apiInstance } from "store/api/axiosApi";
import { repairMojibakeDeep } from "utils/HelperFunctions";
import {
  CANCEL_PAYMENT_PLAN,
  DOWNLOAD_AGREEMENT_PDF,
  DOWNLOAD_PAYMENT,
  GET_COUNT,
  GET_LOGO,
  GET_ORDER_DETAILS,
  GET_PAYMENT_AGREEMENT,
  RESOLVE_LEGACY_AGREEMENT_SLUG,
  RESOLVE_LEGACY_UPDATE_BILLING,
  GET_PAYMENT_HISTORY,
  GET_TRANSACTION_DETAILS,
  VERIFY_REAUTH_PAYMENT,
  GET_PAYMENT_LINK,
  GET_PAYMENT_OFFER,
  GET_PAYMENT_RECEIPT,
  GET_SCHEDULE_HISTORY,
  PAYMENT_PLAN_PREVIEW,
  PROCESS_LINK,
  MAKE_PAYMENT,
  SEND_AGREEMENT_EMAIL,
  SEND_EMAIL_RENEW_PAYMENT_LINK,
  SIGN_AGREEMENT,
  UPDATE_PAYMENT_PLAN,
  CREATE_CASHAPP_PAYMENT_PI,
  GET_PREVIEW_DATA,
  GET_SCHEDULE_ID,
  WIRE_PAYMENT,
  VERIFY_WALLET_PAYMENT,
  GET_PAYMENT_PERMISSION,
  MAKE_PAYMENT_GOOGLEPAY,
  PAYPAL_CREATE_PAY,
  PAYPAL_ONETIME_COMPLETE,
  GET_PAYMENT_DETAILS,
  DOWNLOAD_RECEIPT,
  PRODUCT_LINK_PAYMENT,
  QUICK_CHECKOUT_PAYMENT,
  QUICK_CHECKOUT_CREATE_INTENT,
  GET_GENERAL_SETTINGS,
  GET_CON_FEE,
  GET_MARKERS,
  GET_PORTAL_MARKERS,
  SEND_EMAIL_RENEW_AGREEMENT,
  MAKE_ONETIME_PAYMENT,
  PAYPAL_TOKEN,
  QUICK_CHECKOUT_PAYPAL_TOKEN,
  PAYPAL_PAYMENT_TOKEN,
  CREATE_PLAID_LINK_TOKEN,
  CREATE_PLAID_PAYMENT_TOKEN,
  GET_DUPLICATE_PAYMENT,
  DOWNLOAD_RECEIPT_PDF,
  DOWNLOAD_RECEIPT_AGREEMENT_PDF,
  MARK_INCOMPLETE_ORDER_FAILED,
  VERIFY_STAX_PAYMENT,
  GET_RECEIPT_ID,
  SEND_PAYMENT_ASSISTANCE,
  SEND_SCHEDULE_UPDATED_EMAIL,
} from "store/api/constApi";
import { withSlug } from "utils/currentSlug";
import { isQuickCheckoutRoute } from "utils/quickCheckout";
import type { ConvFeeResponse } from "types/payments";
import type {
  ScheduleUpdatedEmailPayload,
  ScheduleUpdatedEmailResponse,
} from "types/updatePaymentPlan";
import type { LegacyUpdateBillingResolverArgs } from "utils/legacyUpdateBillingRoute";

type WithAbortSignal<T> = T & { __abortSignal?: AbortSignal };

const extractSignal = <T extends Record<string, any>>(
  payload?: WithAbortSignal<T>
) => {
  if (!payload || typeof payload !== "object") {
    return { signal: undefined, data: payload };
  }

  const { __abortSignal, ...data } = payload;
  return { signal: __abortSignal, data };
};

const createCacheKey = (data: any) => {
  if (!data || typeof data !== "object") return "default";

  return JSON.stringify(
    Object.keys(data)
      .sort()
      .reduce((acc: Record<string, any>, key) => {
        acc[key] = data[key];
        return acc;
      }, {})
  );
};

const paymentLogoRequests = new Map<string, Promise<any>>();
const previewDataRequests = new Map<string, Promise<any>>();

const getOncePerReload = (
  requests: Map<string, Promise<any>>,
  key: string,
  request: () => Promise<any>
) => {
  const cachedRequest = requests.get(key);
  if (cachedRequest) return cachedRequest;

  const nextRequest = request();
  requests.set(key, nextRequest);
  return nextRequest;
};

/* ------------------------------------- PAYMENT SERVICES ------------------------------------- */

export const getHistoryCount = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_COUNT, { params: data, signal });
};

export const getPaymentHistory = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_PAYMENT_HISTORY, { params: data, signal });
};

export const getPaymentReceipt = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_PAYMENT_RECEIPT, { params: withSlug(data), signal });
};

export const getTransactionDetails = (
  paymentslug: string,
  signal?: AbortSignal
) =>
  apiInstance.get(GET_TRANSACTION_DETAILS, {
    params: { paymentslug },
    signal,
  });

export const verifyReAuthPayment = (payload: {
  paymentslug: string;
  payment_intent_id: string;
}): Promise<unknown> =>
  apiInstance.post(VERIFY_REAUTH_PAYMENT, payload) as unknown as Promise<unknown>;

export const sendReAuthPaymentEmail = (payload: {
  invoice_id: number;
  accountid?: string | number;
  emails: string[];
  shouldSendAll: boolean;
}): Promise<unknown> =>
  apiInstance.post(DOWNLOAD_PAYMENT, payload) as unknown as Promise<unknown>;

export const getOrderDetails = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_ORDER_DETAILS, { params: data, signal });
};

export const getPaymentLogo = (params: any) => {
  const { signal, data } = extractSignal(params);
  const query = withSlug(data);
  return getOncePerReload(paymentLogoRequests, createCacheKey(query), () =>
    apiInstance.get(GET_LOGO, { params: query, signal })
  );
};

export const getPreviewData = (params: any) => {
  const { signal, data } = extractSignal(params);
  const query = withSlug(data);
  return getOncePerReload(previewDataRequests, createCacheKey(query), () =>
    apiInstance.get(GET_PREVIEW_DATA, { params: query, signal })
  );
};

export const getScheduledHistory = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_SCHEDULE_HISTORY, { params: data, signal });
};

export const downloadPayment = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(DOWNLOAD_PAYMENT, data, { signal });
};

export const downloadReceipt = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(DOWNLOAD_RECEIPT, data, { signal });
};

export const downloadReceiptPdf = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(DOWNLOAD_RECEIPT_PDF, {
    params: data,
    responseType: "blob",
    signal,
  });
};

export const downloadAgreementReceiptPdf = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(DOWNLOAD_RECEIPT_AGREEMENT_PDF, {
    params: data,
    responseType: "blob",
    signal,
  });
};

export const getReceiptID = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_RECEIPT_ID, { params: data, signal });
};

export const updatePaymentPlan = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(UPDATE_PAYMENT_PLAN, data, { signal });
};

export const sendScheduleUpdatedEmail = (
  payload: ScheduleUpdatedEmailPayload
): Promise<ScheduleUpdatedEmailResponse> =>
  apiInstance.post(
    SEND_SCHEDULE_UPDATED_EMAIL,
    payload
  ) as unknown as Promise<ScheduleUpdatedEmailResponse>;

export const getDuplicatePayment = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_DUPLICATE_PAYMENT, { params: data, signal });
};

export const getPaymentPreviewData = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(PAYMENT_PLAN_PREVIEW, { params: data, signal });
};

export const onCancelPaymentPlan = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(CANCEL_PAYMENT_PLAN, data, { signal });
};

export const paymentLinkData = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_PAYMENT_LINK, { params: data, signal });
};

export const sendEmailRenewPaymentLink = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(SEND_EMAIL_RENEW_PAYMENT_LINK, data, { signal });
};

export const sendEmailRenewAgreement = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(SEND_EMAIL_RENEW_AGREEMENT, data, { signal });
};

export const processPaymentLink = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(PROCESS_LINK, data, { signal });
};

export const paymentAgreementData = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_PAYMENT_AGREEMENT, { params: data, signal });
};

export const resolveLegacyAgreementSlug = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(RESOLVE_LEGACY_AGREEMENT_SLUG, {
    params: data,
    signal,
  });
};

export const resolveLegacyUpdateBilling = (
  payload: LegacyUpdateBillingResolverArgs
) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(RESOLVE_LEGACY_UPDATE_BILLING, data, { signal });
};

export const signAgreementPayment = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(SIGN_AGREEMENT, data, { signal });
};

export const getPaymentOffer = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance
    .get(GET_PAYMENT_OFFER, { params: withSlug(data), signal })
    .then(repairMojibakeDeep);
};

export const processMakePayment = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(MAKE_PAYMENT, data, { signal });
};

export const downloadAgreementFile = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(DOWNLOAD_AGREEMENT_PDF, { params: data, signal });
};

export const sendAgreementEmail = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(SEND_AGREEMENT_EMAIL, withSlug(data), { signal });
};

export const googlePayMakePayment = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(MAKE_PAYMENT_GOOGLEPAY, data, { signal });
};

export const makeOneTimePayment = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(MAKE_ONETIME_PAYMENT, withSlug(data), { signal });
};

export const payPalIntent = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(PAYPAL_CREATE_PAY, data, { signal });
};

export const payPalCompleteOneTime = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(PAYPAL_ONETIME_COMPLETE, {
    params: withSlug(data),
    signal,
  });
};

export const WirePayment = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(WIRE_PAYMENT, data, { signal });
};

export const paymentPermission = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(GET_PAYMENT_PERMISSION, data, { signal });
};

export const createCashAppPI = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  // Quick checkout guests have no account yet, which create-payment-intents
  // requires; its own endpoint creates the intent for the cluster instead.
  if (isQuickCheckoutRoute()) {
    return apiInstance.post(QUICK_CHECKOUT_CREATE_INTENT, withSlug(data), {
      signal,
    });
  }
  return apiInstance.post(CREATE_CASHAPP_PAYMENT_PI, withSlug(data), {
    signal,
  });
};

export const createPlaidLinkToken = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(CREATE_PLAID_LINK_TOKEN, withSlug(data), { signal });
};

export const createPlaidPaymentToken = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(CREATE_PLAID_PAYMENT_TOKEN, withSlug(data), {
    signal,
  });
};

export const createpaypalPaymentToken = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(PAYPAL_PAYMENT_TOKEN, withSlug(data), { signal });
};

export const createpaypalToken = async (payload: any) => {
  const { signal, data } = extractSignal(payload);
  if (!isQuickCheckoutRoute()) {
    return apiInstance.post(PAYPAL_TOKEN, data, { signal });
  }
  // Quick checkout has its own PayPal setup endpoint (cluster from the slug,
  // no account email). Until the payment API that has it is deployed, the
  // route does not exist (a 404 that is not an API answer): use the old one.
  try {
    return await apiInstance.post(
      QUICK_CHECKOUT_PAYPAL_TOKEN,
      withSlug(data),
      { signal }
    );
  } catch (error: any) {
    const isMissingRoute =
      error?.response?.status === 404 && !error?.response?.data?.body;
    if (!isMissingRoute) throw error;
    return apiInstance.post(PAYPAL_TOKEN, withSlug(data), { signal });
  }
};

export const verifyWalletPayments = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(VERIFY_WALLET_PAYMENT, data, { signal });
};

export const getScheduleID = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_SCHEDULE_ID, { params: withSlug(data), signal });
};

export const getPaymentHistoryDetails = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_PAYMENT_DETAILS, { params: data, signal });
};

export const productLinkPaymnet = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(PRODUCT_LINK_PAYMENT, withSlug(data), { signal });
};

export const quickCheckoutPayment = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(QUICK_CHECKOUT_PAYMENT, withSlug(data), { signal });
};

export const getGeneralSettings = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_GENERAL_SETTINGS, { params: data, signal });
};

export const getConvFee = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get<ConvFeeResponse, ConvFeeResponse>(GET_CON_FEE, {
    params: withSlug(data),
    signal,
  });
};

export const markIncompleteOrderFailed = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(MARK_INCOMPLETE_ORDER_FAILED, withSlug(data), {
    signal,
  });
};

export const verifyStaxPayment = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(VERIFY_STAX_PAYMENT, data, { signal });
};

export const getPaymentMarkers = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_MARKERS, { params: data, signal });
};

export const getCompanyMarkers = (params: any) => {
  const { signal, data } = extractSignal(params);
  return apiInstance.get(GET_PORTAL_MARKERS, { params: withSlug(data), signal });
};

export const sendPaymentAssistance = (payload: any) => {
  const { signal, data } = extractSignal(payload);
  return apiInstance.post(SEND_PAYMENT_ASSISTANCE, data, { signal });
}
